<template>
  <div v-if="visible" ref="rootEl" class="toast-notification">
    <cv-toast-notification
      :class="cssClass"
      :kind="params.kind"
      :title="params.title"
      :low-contrast="lowContrast"
      @close="onClose(params)"
    >
      <template #subtitle>
        <div v-safe-html="params.subTitle" />
      </template>

      <template #caption>
        <div v-safe-html="params.caption" />
      </template>
    </cv-toast-notification>
  </div>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils";
import ToastEventBus from "@plugins/toast/service/event-bus.ts";
import { ref, computed, onMounted, onUnmounted } from "vue";

// Props
const props = defineProps({
  params: { type: Object, default: null },
});

// Data
const visible = ref(true);
const timer = ref<ReturnType<typeof setTimeout> | null>(null);
const lowContrast = ref(true);
const rootEl = ref<HTMLElement | null>(null);

// Computed
const cssClass = computed(() => [
  {
    "no-subtitle": props.params.subTitle === "",
    "no-caption": props.params.caption === "",
    "hide-close-button": props.params.hideCloseButton,
  },
  props.params.cssClass ? props.params.cssClass : "",
]);

const toastTimeout = computed(() => props.params.timeout ?? 5000);

// Methods
const onClose = (params) => {
  if (typeof params.actionClose !== "undefined" && typeof params.actionClose === "function") {
    params.actionClose();
  }
  if (timer.value) clearTimeout(timer.value);
  ToastEventBus.$emit("remove-notification", params.toastNotificationId);
  visible.value = false;
};

const captionEvent = (e: Event) => {
  ToastEventBus.$emit("remove-notification", props.params.toastNotificationId);
  visible.value = false;
  props.params.captionEvent?.(e);
};

// Mounted
onMounted(() => {
  if (props.params.hasTimeout) {
    timer.value = setTimeout(() => {
      ToastEventBus.$emit("remove-notification", props.params.toastNotificationId);
      visible.value = false;
    }, toastTimeout.value);
  }

  if (props.params.caption && props.params.captionEvent && rootEl.value) {
    DOMUtils.elOf(rootEl.value, ".bx--toast-notification__caption > a").forEach((el: Element) => {
      el.removeEventListener("click", captionEvent);
      el.addEventListener("click", captionEvent);
    });
  }
});

onUnmounted(() => {
  if (timer.value) clearTimeout(timer.value);

  if (props.params.caption && props.params.captionEvent && rootEl.value) {
    DOMUtils.elOf(rootEl.value, ".bx--toast-notification__caption > a").forEach((el: Element) => {
      el.removeEventListener("click", captionEvent);
    });
  }
});
</script>

<style lang="scss">
@use "@styles/layout/breakpoint" as *;
@use "@styles/layout/z-map" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.toast-notification {
  position: relative;
  display: flex;
  justify-content: center;
  // Default width value from Carbon
  width: 600px;
  max-width: 100%;
  top: 0;
  right: 0;
  z-index: z-map("toast-notification");
  animation: slide 500ms ease-in-out;

  &:has(.bx--toast-notification__caption) {
    .bx--toast-notification__title {
      max-width: calc(100% - $spacing-09);
    }
  }

  @keyframes slide {
    0% {
      right: -10rem;
    }

    100% {
      right: 0;
    }
  }

  .bx--toast-notification {
    box-shadow: $shadow-02;
    border-radius: 10px;
    min-width: 350px;
    width: 600px;
    @include media("mobile") {
      width: calc(100% - 2rem);
      margin: 0 1rem;
    }

    &.no-subtitle {
      &.no-caption {
        .bx--toast-notification__title {
          margin-bottom: $spacing-05;
        }
      }

      .bx--toast-notification__subtitle {
        display: none;
      }
    }

    &.no-caption {
      .bx--toast-notification__title {
        max-width: 100%;
      }

      .bx--toast-notification__caption {
        display: none;
      }
    }

    &.hide-close-button {
      .bx--toast-notification__close-button {
        display: none;
      }
    }

    &.bx--toast-notification--success {
      background: $support-background-02;
    }

    .bx--toast-notification__close-button {
      .bx--toast-notification__close-icon {
        fill: $interactive-02;
      }
    }

    .bx--toast-notification__details {
      position: relative;
      width: 100%;
      padding-left: $spacing-08;
      margin-right: 0; // overwrite default margin to have more text space

      &:before {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
      }

      .bx--toast-notification__subtitle {
        margin-bottom: $spacing-05;
      }
    }

    .bx--toast-notification__caption {
      padding-top: 0;
    }
  }

  .notebook-toast {
    .bx--toast-notification__details {
      flex: 1 0 auto;
      max-width: calc(100% - 4rem);
      padding-right: $spacing-08;
    }

    .bx--toast-notification__caption {
      @include carbon--type-style("button-02");
      position: absolute;
      right: 0;
      top: 14px;
      margin-bottom: 0;
      cursor: pointer;
    }

    .bx--toast-notification__title {
      margin: $spacing-05 0;
    }
  }
}
</style>
